/* eslint-disable @wyz/module/no-relative-import */
import {
  Button,
  Flex,
  FormFeedback,
  FormGroup,
  Label,
  SelectLoadingIndicator,
  Switch,
} from '@wyz/ui';
import clsx from 'clsx';
import { Trans, useI18n } from '~/common/context';
import { TranslationKeys } from '~/common/types';
import { SeasonIcon } from '~/purchase/components';
import './season-selector.scss';
import { ISeason } from '~/purchase/types';

type SeasonSelectorProps = {
  seasons: Array<string>;
  availableSeasons: Array<ISeason>;
  required: boolean;
  isLoading: boolean;
  isDisabled?: boolean;
  disabledSeasons?: Array<string>;
  onChange: (seasons: Array<string>) => void;
  errorMessage?: string;
  label?: keyof TranslationKeys;
  showSwitch?: boolean;
};

function SeasonSelector({
  seasons = [],
  availableSeasons,
  required,
  isLoading,
  isDisabled = false,
  disabledSeasons,
  onChange,
  errorMessage,
  label = 'app.common.product.season',
  showSwitch = false,
}: SeasonSelectorProps) {
  const { t } = useI18n();

  return (
    <FormGroup>
      <Label
        className={clsx({
          'required': required,
          'text-danger-text': Boolean(errorMessage),
        })}
      >
        <Trans i18nKey={label} />
      </Label>
      {isLoading && <SelectLoadingIndicator />}
      {!isLoading && (
        <Flex
          alignItems='center'
          justifyContent='around'
          wrap='wrap'
          className='gap-3 season-selector'
        >
          {availableSeasons.map((item) => {
            const isSelected = seasons.includes(item.publicKey);
            const disabled =
              isDisabled ||
              (disabledSeasons?.includes(item.publicKey) ?? false);

            const toggle = () => {
              if (disabled) return;
              const newSeasons = isSelected
                ? seasons.filter((s) => s !== item.publicKey)
                : [...seasons, item.publicKey];
              onChange(newSeasons);
            };

            return (
              <Flex
                key={item.publicKey}
                alignItems='center'
                className='gap-2 flex-1'
              >
                {showSwitch && (
                  <Switch
                    id={`season-selector-switch-${t(item.i18nKey)}`}
                    size='md'
                    checked={isSelected}
                    onChange={toggle}
                  />
                )}
                <div className='flex-1'>
                  <Button
                    id={`season-selector-button-${t(item.i18nKey)}`}
                    type='button'
                    size='sm'
                    disabled={disabled}
                    variant={isSelected ? 'primary' : 'secondary'}
                    fullWidth
                    onClick={toggle}
                  >
                    <SeasonIcon
                      value={item.id}
                      size='xs'
                      className={
                        isSelected ? 'season-icon-selected' : undefined
                      }
                    />
                    <span
                      className={clsx(
                        'text-xs',
                        'text-truncate',
                        'season-text',
                      )}
                    >
                      {t(item.i18nKey)}
                    </span>
                  </Button>
                </div>
              </Flex>
            );
          })}
        </Flex>
      )}
      {errorMessage && (
        <FormFeedback invalid>
          {t(errorMessage as keyof TranslationKeys)}
        </FormFeedback>
      )}
    </FormGroup>
  );
}

export default SeasonSelector;
